import React, { useState, useEffect, useCallback } from 'react';
import { ChevronRight, Edit, Trash2, AlertTriangle, Loader, Calendar, Users, Clock, Link as LinkIcon, Plus, X, ExternalLink } from 'lucide-react';
import ApiService from './ApiService';

const ServiceDetailPage = ({ service, config, onBack, onServiceDeleted, onServiceUpdated }) => {
    const [patientsUsingService, setPatientsUsingService] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showAddLink, setShowAddLink] = useState(false);
    const [newLink, setNewLink] = useState({ title: '', url: '' });
    const [savingLink, setSavingLink] = useState(false);

    const t = config?.terminology || {
        service: { singular: 'Service', plural: 'Services', singularLower: 'service', pluralLower: 'services' },
        client: { singular: 'Patient', plural: 'Patients', singularLower: 'patient', pluralLower: 'patients' }
    };

    useEffect(() => {
        loadPatientsUsingService();
    }, [service.serviceId]);

    const loadPatientsUsingService = useCallback(async () => {
        try {
            setLoading(true);
            const data = await ApiService.checkServiceAssignments(service.serviceId);
            setPatientsUsingService(data.patients || []);
        } catch (err) {
            console.error('Failed to load patients:', err);
            setPatientsUsingService([]);
        } finally {
            setLoading(false);
        }
    }, [service.serviceId]);

    const handleDeleteService = async () => {
        setDeleting(true);
        try {
            await ApiService.deleteService(service.serviceId);
            onServiceDeleted(service.serviceId);
            onBack();
        } catch (err) {
            console.error('Failed to delete service:', err);
            alert('Failed to delete service. Please try again.');
        } finally {
            setDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    const handleAddLink = async () => {
        if (!newLink.title.trim() || !newLink.url.trim()) {
            alert('Please provide both title and URL');
            return;
        }

        // Validate URL format
        try {
            new URL(newLink.url);
        } catch (e) {
            alert('Please provide a valid URL (e.g., https://youtube.com/watch?v=...)');
            return;
        }

        setSavingLink(true);
        try {
            const links = service.links || [];
            links.push({
                id: Date.now().toString(),
                title: newLink.title.trim(),
                url: newLink.url.trim(),
                addedAt: new Date().toISOString()
            });

            await ApiService.updateService(service.serviceId, { links });
            
            // Update local service object
            service.links = links;
            if (onServiceUpdated) {
                onServiceUpdated({ ...service, links });
            }

            setNewLink({ title: '', url: '' });
            setShowAddLink(false);
        } catch (err) {
            console.error('Failed to add link:', err);
            alert('Failed to add link. Please try again.');
        } finally {
            setSavingLink(false);
        }
    };

    const handleDeleteLink = async (linkId) => {
        if (!window.confirm('Remove this link?')) return;

        try {
            const links = (service.links || []).filter(link => link.id !== linkId);
            await ApiService.updateService(service.serviceId, { links });
            
            service.links = links;
            if (onServiceUpdated) {
                onServiceUpdated({ ...service, links });
            }
        } catch (err) {
            console.error('Failed to delete link:', err);
            alert('Failed to delete link. Please try again.');
        }
    };

    const getCategoryColor = (category) => {
        const colors = {
            'Hormone Therapy': 'bg-pink-100 text-pink-800',
            'Diagnostic': 'bg-spruce-100 text-spruce-800',
            'Appointment': 'bg-green-100 text-green-800',
            'Treatment': 'bg-spruce-100 text-spruce-800',
            'Monitoring': 'bg-yellow-100 text-yellow-800',
            'Other': 'bg-gray-100 text-gray-800'
        };
        return colors[category] || 'bg-gray-100 text-gray-800';
    };

    if (!service) return null;

    const serviceLinks = service.links || [];

    return (
        <div className="space-y-6">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-gray-500">
                <button onClick={onBack} className="hover:text-spruce-600">
                    {t.service.plural}
                </button>
                <ChevronRight size={16} />
                <span>{service.name}</span>
            </div>

            {/* Service Header */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex justify-between items-start">
                    <div className="flex items-start gap-4">
                        <div className="p-4 bg-orange-100 rounded-lg">
                            <Calendar className="text-orange-600" size={32} />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-gray-800">{service.name}</h2>
                            <div className="flex gap-3 mt-2">
                                {service.category && (
                                    <span className={`px-3 py-1 rounded-full text-sm ${getCategoryColor(service.category)}`}>
                                        {service.category}
                                    </span>
                                )}
                                <span className="bg-spruce-100 text-spruce-800 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                                    <Clock size={14} />
                                    Every {service.intervalMonths} month{service.intervalMonths > 1 ? 's' : ''}
                                </span>
                                <span className="bg-spruce-100 text-spruce-800 px-3 py-1 rounded-full text-sm">
                                    Remind {service.reminderDaysBefore} days before
                                </span>
                            </div>
                            {service.description && (
                                <p className="mt-3 text-gray-600">{service.description}</p>
                            )}
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button className="flex items-center gap-2 px-4 py-2 text-spruce-600 hover:bg-spruce-50 rounded-lg transition">
                            <Edit size={18} />
                            Edit
                        </button>
                        <button
                            onClick={() => setShowDeleteConfirm(true)}
                            className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                        >
                            <Trash2 size={18} />
                            Delete
                        </button>
                    </div>
                </div>
            </div>

            {/* Educational Links Section */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-2">
                        <LinkIcon className="text-spruce-600" size={20} />
                        <h3 className="text-lg font-semibold text-gray-800">Preparation & Information Links</h3>
                    </div>
                    <button
                        onClick={() => setShowAddLink(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-spruce-600 text-white rounded-lg hover:bg-spruce-700 transition"
                    >
                        <Plus size={18} />
                        Add Link
                    </button>
                </div>

                {serviceLinks.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">
                        No information links added yet. Add preparation guides, instructional videos, or other resources for patients.
                    </p>
                ) : (
                    <div className="space-y-3">
                        {serviceLinks.map((link) => (
                            <div
                                key={link.id}
                                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                            >
                                <div className="flex items-center gap-3 flex-1">
                                    <LinkIcon className="text-gray-400" size={18} />
                                    <div className="flex-1">
                                        <h4 className="font-medium text-gray-800">{link.title}</h4>
                                        <a
                                            href={link.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-sm text-spruce-600 hover:text-spruce-800 flex items-center gap-1"
                                        >
                                            {link.url}
                                            <ExternalLink size={12} />
                                        </a>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleDeleteLink(link.id)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded transition"
                                    title="Remove link"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <div className="mt-4 p-3 bg-spruce-50 border border-spruce-200 rounded">
                    <p className="text-sm text-spruce-900">
                        💡 <strong>Note:</strong> When scheduling this {t.service.singularLower} for a {t.client.singularLower}, 
                        you'll have the option to include these links in reminder messages.
                    </p>
                </div>
            </div>

            {/* Add Link Modal */}
            {showAddLink && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <h3 className="text-xl font-bold text-gray-800 mb-4">Add Information Link</h3>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Link Title
                                </label>
                                <input
                                    type="text"
                                    value={newLink.title}
                                    onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
                                    placeholder="e.g., Blood work preparation guide"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    URL
                                </label>
                                <input
                                    type="url"
                                    value={newLink.url}
                                    onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                                    placeholder="https://youtube.com/watch?v=..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500 focus:border-transparent"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Paste a YouTube video, article, or any informational resource URL
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowAddLink(false);
                                    setNewLink({ title: '', url: '' });
                                }}
                                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddLink}
                                disabled={savingLink}
                                className="flex-1 px-4 py-2 bg-spruce-600 text-white rounded-lg hover:bg-spruce-700 disabled:opacity-50 transition"
                            >
                                {savingLink ? 'Adding...' : 'Add Link'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Patients Using This Service */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center gap-2 mb-4">
                    <Users className="text-gray-600" size={20} />
                    <h3 className="text-lg font-semibold text-gray-800">
                        {t.client.plural} Scheduled for This {t.service.singular}
                    </h3>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader className="animate-spin text-spruce-600" size={24} />
                    </div>
                ) : patientsUsingService.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">
                        No {t.client.pluralLower} are currently scheduled for this {t.service.singularLower}.
                    </p>
                ) : (
                    <div className="divide-y divide-gray-200">
                        {patientsUsingService.map((patient) => (
                            <div key={patient.patientId} className="py-3 flex items-center justify-between hover:bg-gray-50 px-3 rounded">
                                <div>
                                    <p className="font-medium text-gray-800">{patient.name}</p>
                                    <p className="text-sm text-gray-500">
                                        Next due: {patient.nextDueDate ? new Date(patient.nextDueDate).toLocaleDateString() : 'Not scheduled'}
                                    </p>
                                </div>
                                <Calendar className="text-gray-400" size={18} />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 bg-red-100 rounded-full">
                                <AlertTriangle className="text-red-600" size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800">Delete {t.service.singular}?</h3>
                        </div>
                        <p className="text-gray-600 mb-4">
                            Are you sure you want to delete <strong>{service.name}</strong>?
                        </p>
                        {patientsUsingService.length > 0 && (
                            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                                <p className="text-sm text-yellow-900">
                                    <strong>{patientsUsingService.length} {t.client.pluralLower}</strong> are currently scheduled for this service. 
                                    Their existing appointments will be preserved but you won't be able to schedule this service for new patients.
                                </p>
                            </div>
                        )}
                        <p className="text-red-600 font-semibold mb-6">This action cannot be undone.</p>
                        <div className="flex gap-3">
                            <button
                                onClick={handleDeleteService}
                                disabled={deleting}
                                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50 transition flex items-center justify-center gap-2"
                            >
                                {deleting && <Loader className="animate-spin" size={16} />}
                                Delete {t.service.singular}
                            </button>
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                disabled={deleting}
                                className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ServiceDetailPage;